import React, { Component, createRef } from 'react'
import { observable } from 'mobx'
import { observer } from 'mobx-react'
import { array } from 'prop-types'
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps'
import { Icon } from '@winnin/winnin_ui.components.icon'

import { COLORS } from 'consts'
import { numberUtils } from 'utils'

import './index.scss'

@observer
class WorldMap extends Component {
  @observable
  zoom = 1
  @observable
  center = [0, 0]
  @observable
  showTooltip = false
  @observable
  tooltip = {}
  MAX_ZOOM = 12
  MIN_ZOOM = 1
  zoomRef = createRef()
  divRef = createRef()

  preventDefault = e => e.preventDefault()

  handleMoveEnd(position) {
    this.center = position.coordinates
  }

  zoomIn = () => {
    const next = this.zoom * 1.5
    if (next <= this.MAX_ZOOM) this.zoom = next
    // This is used to reset the zoom when the user fully zoom out
    if (next === this.MIN_ZOOM * 1.5) this.center = [0, 1]
  }

  zoomOut = () => {
    const next = this.zoom / 1.5
    if (next >= this.MIN_ZOOM) this.zoom = next
    // This is used to reset the zoom when the user fully zoom out
    if (next === this.MIN_ZOOM) this.center = [0, 0]
  }

  decideZoom = e => {
    if (e.deltaY < 0) this.zoomIn()
    if (e.deltaY > 0) this.zoomOut()
  }

  componentDidMount() {
    this.divRef.current.addEventListener('wheel', this.preventDefault)
  }

  componentWillUnmount() {
    this.divRef.current.removeEventListener('wheel', this.preventDefault)
  }

  isHighlighted = c => {
    const { data } = this.props
    return !!data.find(v => v.country.toLowerCase() === c.toLowerCase())
  }

  getColorForCountry = c => {
    if (this.isHighlighted(c)) return COLORS.primary
    return COLORS.gray
  }

  getHoverColorForCountry = c => {
    if (this.isHighlighted(c)) return COLORS.primary
    return COLORS.gray
  }

  geographyMove = (evt, geography) => {
    const { data } = this.props
    const x = evt.nativeEvent.offsetX
    const y = evt.nativeEvent.offsetY

    const country = geography.properties.NAME
    const object = data.find(v => v.country.toLowerCase() === geography.properties.ISO_A2.toLowerCase())

    if (object) {
      this.showTooltip = true
      this.tooltip = {
        x,
        y,
        title: country,
        data: {
          percent: numberUtils.formatPercent(object.percent, 1),
        },
      }
    }
  }

  geographyLeave = () => {
    this.showTooltip = false
  }

  render() {
    return (
      <div className="world-map-container">
        {this.showTooltip && this.tooltip.data && (
          <div className="tooltip-component" style={{ left: this.tooltip.x, top: this.tooltip.y }}>
            {this.tooltip.title && <div className="tooltip-title">{this.tooltip.title}</div>}
            <div className="tooltip-series-group">
              <div className="tooltip-text">
                {Object.keys(this.tooltip.data).map(v => (
                  <div key={v}>
                    <span className="tooltip-data-title">{`${v}: `}</span>
                    <span className="tooltip-value">{this.tooltip.data[v]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        <div onWheel={this.decideZoom} className="world-map" ref={this.divRef}>
          <button className="zoom-button zoom-in" onClick={this.zoomIn}>
            <Icon icon="add" className="zoom-icon" />
          </button>
          <button className="zoom-button zoom-out" onClick={this.zoomOut}>
            <Icon icon="remove" className="zoom-icon" />
          </button>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 95,
              rotate: [-10, 0, 0],
              center: [-10, 15],
            }}
            width={630}
            height={322}
          >
            <ZoomableGroup
              ref={this.zoomRef}
              zoom={this.zoom}
              center={this.center}
              translateExtent={[
                [0, -322],
                [630, 322],
              ]}
              filterZoomEvent={evt => {
                return evt.type === 'wheel' ? false : true
              }}
              onMoveEnd={position => this.handleMoveEnd(position)}
              maxZoom={this.zoom}
              minZoom={this.zoom}
            >
              <Geographies geography="https://files.winnin.com/interface/insight/map/world-110m.json">
                {({ geographies, projection }) =>
                  geographies.map(geography => {
                    return (
                      geography.properties.ISO_A3 !== 'ATA' && (
                        <Geography
                          key={geography.rsmKey}
                          geography={geography}
                          projection={projection}
                          className={`country ${geography.properties.ISO_A3}`}
                          onMouseMove={e => this.geographyMove(e, geography)}
                          onMouseLeave={this.geographyLeave}
                          style={{
                            default: {
                              fill: this.getColorForCountry(geography.properties.ISO_A2),
                              strokeWidth: 0,
                              outline: 'none',
                            },
                            hover: {
                              fill: this.getHoverColorForCountry(geography.properties.ISO_A2),
                              strokeWidth: 0,
                              outline: 'none',
                            },
                            pressed: {
                              fill: this.getHoverColorForCountry(geography.properties.ISO_A2),
                              strokeWidth: 0,
                              outline: 'none',
                            },
                          }}
                        />
                      )
                    )
                  })
                }
              </Geographies>
            </ZoomableGroup>
          </ComposableMap>
        </div>
      </div>
    )
  }
}

WorldMap.propTypes = {
  data: array,
}

WorldMap.defaultProps = {}

WorldMap.displayName = 'WorldMap'

export default WorldMap
